// Spot Sell Modal - Professional UI for selling spot assets
import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { X, TrendingDown, Wallet, DollarSign, AlertTriangle, Percent } from 'lucide-react';
import { tradingAPI } from '../../services/api';
import { useTradingToast } from '../../hooks/useTradingToast';

const SpotSellModal = ({ isOpen, onClose, holding, onSuccess }) => {
  const [quantity, setQuantity] = useState('');
  const [currentPrice, setCurrentPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useTradingToast();

  useEffect(() => {
    if (isOpen && holding) {
      setQuantity('');
      loadCurrentPrice();
    }
  }, [isOpen, holding]);

  const loadCurrentPrice = async () => {
    if (!holding) return;
    try {
      const symbol = `${holding.symbol}USDT`;
      const response = await tradingAPI.getPrice(symbol);
      setCurrentPrice(response.data.price);
    } catch (error) {
      console.error('Failed to load price:', error);
      // Use avg_buy_price as fallback
      setCurrentPrice(holding.avg_buy_price);
    }
  };

  if (!isOpen || !holding) return null;

  const maxQuantity = holding.quantity || 0;
  const avgBuyPrice = holding.avg_buy_price || 0;

  // Calculate estimated proceeds and P&L
  const sellQuantity = parseFloat(quantity) || 0;
  const sellValue = sellQuantity * (currentPrice || avgBuyPrice);
  const costBasis = sellQuantity * avgBuyPrice;
  const estimatedPnL = sellValue - costBasis;
  const pnlPercent = costBasis > 0 ? (estimatedPnL / costBasis * 100) : 0;

  const handleQuickAmount = (percent) => {
    const qty = maxQuantity * percent / 100;
    setQuantity(qty.toString());
  };

  const handleSubmit = async () => {
    if (!quantity || parseFloat(quantity) <= 0) {
      toast.error('Введите количество');
      return;
    }

    if (parseFloat(quantity) > maxQuantity) {
      toast.error(`Максимум: ${maxQuantity.toFixed(6)} ${holding.symbol}`);
      return;
    }

    setLoading(true);
    try {
      await tradingAPI.spotSell({
        symbol: holding.symbol,
        quantity: parseFloat(quantity),
      });

      toast.success(
        'Продажа выполнена', 
        `Продано ${parseFloat(quantity).toFixed(6)} ${holding.symbol} за $${sellValue.toFixed(2)}`
      );

      onClose();
      onSuccess?.();
    } catch (error) {
      toast.apiError(error, 'Ошибка при продаже');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '$0.00';
    if (price >= 1) return `$${price.toFixed(2)}`;
    return `$${price.toPrecision(4)}`;
  };

  const formatQuantity = (qty) => {
    if (qty >= 1000) return qty.toFixed(2);
    if (qty >= 1) return qty.toFixed(4);
    return qty.toFixed(6);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#1e2329',
          borderRadius: '16px',
          width: '420px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.5)',
          border: '1px solid #2b3139',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #2b3139',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(180deg, #2b1f1f 0%, #1e2329 100%)',
        }}>
          <div>
            <h2 style={{
              margin: 0,
              color: '#eaecef',
              fontSize: '18px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <TrendingDown size={20} color="#f6465d" />
              Продать {holding.symbol}
            </h2>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginTop: '4px',
            }}>
              <span style={{ color: '#848e9c', fontSize: '14px' }}>Текущая цена:</span>
              <span style={{
                color: '#f0b90b',
                fontSize: '16px',
                fontWeight: '700',
                fontFamily: 'monospace',
              }}>
                {formatPrice(currentPrice)}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#2b3139',
              border: 'none',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              color: '#848e9c',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px' }}>
          {/* Asset Info */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
            padding: '14px 16px',
            backgroundColor: '#161a1e',
            borderRadius: '10px',
            marginBottom: '16px',
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <Wallet size={14} color="#848e9c" />
                <span style={{ color: '#848e9c', fontSize: '12px' }}>Доступно:</span>
              </div>
              <span style={{ color: '#eaecef', fontSize: '15px', fontWeight: '700', fontFamily: 'monospace' }}>
                {formatQuantity(maxQuantity)} {holding.symbol}
              </span>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <DollarSign size={14} color="#848e9c" />
                <span style={{ color: '#848e9c', fontSize: '12px' }}>Ср. цена покупки:</span>
              </div>
              <span style={{ color: '#eaecef', fontSize: '15px', fontWeight: '700', fontFamily: 'monospace' }}>
                {formatPrice(avgBuyPrice)}
              </span>
            </div>
          </div>

          {/* Quantity Input */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ color: '#848e9c', fontSize: '12px', marginBottom: '6px', display: 'block' }}>
              Количество для продажи
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0.00"
                style={{
                  width: '100%',
                  padding: '14px 80px 14px 16px',
                  backgroundColor: '#161a1e',
                  border: '1px solid #2b3139',
                  borderRadius: '10px',
                  color: '#eaecef',
                  fontSize: '18px',
                  fontFamily: 'monospace',
                  fontWeight: '600',
                  outline: 'none',
                }}
              />
              <span style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#f0b90b',
                fontSize: '14px',
                fontWeight: '600',
              }}>
                {holding.symbol}
              </span>
            </div>

            {/* Quick Amount Buttons */}
            <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
              {[25, 50, 75, 100].map((pct) => (
                <button
                  key={pct}
                  onClick={() => handleQuickAmount(pct)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: '#161a1e',
                    border: '1px solid #2b3139',
                    borderRadius: '8px',
                    color: '#848e9c',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#252930';
                    e.currentTarget.style.color = '#f6465d';
                    e.currentTarget.style.borderColor = '#f6465d';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#161a1e';
                    e.currentTarget.style.color = '#848e9c';
                    e.currentTarget.style.borderColor = '#2b3139';
                  }}
                >
                  {pct === 100 ? 'MAX' : `${pct}%`}
                </button>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          {sellQuantity > 0 && (
            <div style={{
              backgroundColor: '#161a1e',
              borderRadius: '10px',
              padding: '14px 16px',
              marginBottom: '16px',
            }}>
              <div style={{ fontSize: '12px', color: '#848e9c', marginBottom: '12px', fontWeight: '600' }}>
                Детали продажи
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ color: '#848e9c', fontSize: '13px' }}>Продаёте</span>
                <span style={{ color: '#eaecef', fontSize: '13px', fontFamily: 'monospace', fontWeight: '600' }}>
                  {formatQuantity(sellQuantity)} {holding.symbol}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ color: '#848e9c', fontSize: '13px' }}>Цена продажи</span>
                <span style={{ color: '#eaecef', fontSize: '13px', fontFamily: 'monospace' }}>
                  {formatPrice(currentPrice)}
                </span>
              </div>

              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '10px',
                paddingTop: '10px',
                borderTop: '1px solid #2b3139',
              }}>
                <span style={{ color: '#848e9c', fontSize: '13px' }}>Получите</span>
                <span style={{ color: '#0ecb81', fontSize: '15px', fontFamily: 'monospace', fontWeight: '700' }}>
                  ${sellValue.toFixed(2)} USDT
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#848e9c', fontSize: '13px' }}>Расчётный P&L</span>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ 
                    color: estimatedPnL >= 0 ? '#0ecb81' : '#f6465d', 
                    fontSize: '13px', 
                    fontFamily: 'monospace',
                    fontWeight: '600',
                  }}>
                    {estimatedPnL >= 0 ? '+' : ''}{estimatedPnL.toFixed(2)} USDT
                  </span>
                  <span style={{
                    marginLeft: '8px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    backgroundColor: estimatedPnL >= 0 ? '#0ecb8115' : '#f6465d15',
                    color: estimatedPnL >= 0 ? '#0ecb81' : '#f6465d',
                  }}>
                    {estimatedPnL >= 0 ? '+' : ''}{pnlPercent.toFixed(2)}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Warning */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            padding: '12px',
            backgroundColor: '#f6465d15',
            borderRadius: '8px',
            marginBottom: '16px',
            border: '1px solid #f6465d30',
          }}>
            <AlertTriangle size={16} color="#f6465d" style={{ flexShrink: 0, marginTop: '2px' }} />
            <span style={{ fontSize: '12px', color: '#848e9c', lineHeight: '1.5' }}>
              Продажа будет выполнена по рыночной цене. Фактическая цена может немного отличаться.
            </span>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading || !quantity || parseFloat(quantity) <= 0 || parseFloat(quantity) > maxQuantity}
            style={{
              width: '100%',
              padding: '16px',
              background: loading || !quantity || parseFloat(quantity) <= 0 || parseFloat(quantity) > maxQuantity
                ? '#2b3139'
                : 'linear-gradient(180deg, #f6465d 0%, #cf304a 100%)',
              color: loading || !quantity || parseFloat(quantity) <= 0 || parseFloat(quantity) > maxQuantity
                ? '#5e6673'
                : '#fff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: loading || !quantity || parseFloat(quantity) <= 0 || parseFloat(quantity) > maxQuantity
                ? 'not-allowed'
                : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              transition: 'all 0.2s',
              boxShadow: loading || !quantity ? 'none' : '0 4px 20px rgba(246, 70, 93, 0.3)',
            }}
          >
            {loading ? (
              'Выполнение...'
            ) : (
              <>
                <TrendingDown size={20} />
                Продать {holding.symbol}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpotSellModal;
